import type { ForwardedRef, InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useEffect, useState } from 'react';

import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

import type { ComponentProps } from '../ComponentProps';
import { getClassNameOverride } from '../getClassNameOverride';
import { Tag } from '../Tag';

interface RadioGroupOption {
  value: string;
  label: ReactNode;
}

type RadioGroupVariant = 'tag' | 'radio';

const radioGroup = variants({
  slots: {
    wrapper: cn('inline-flex items-center gap-sm'),
    input: cn(
      'relative h-md w-md cursor-pointer appearance-none rounded-full border transition-colors border-default',
      "after:absolute after:inset-[4px] after:scale-0 after:rounded-full after:transition-transform after:content-['']",
      'checked:border-secondary-default checked:after:scale-100 checked:after:bg-secondary-default',
    ),
  },
  variants: {
    variant: {
      tag: {
        input: cn('sr-only'),
      },
      radio: {},
    },
  },
});

export type RadioGroupProps = ComponentProps<
  InputHTMLAttributes<HTMLInputElement>
> & {
  options: RadioGroupOption[];
  variant?: RadioGroupVariant;
};

export const RadioGroup = forwardRef(
  (
    {
      options,
      name,
      dangerouslyOverrideClassName,
      value,
      disabled,
      readOnly,
      variant = 'tag',
      ...inputProps
    }: RadioGroupProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    // Used only for styling, value is controlled by the input or RHF
    // This is so that the component can be used in controlled and uncontrolled forms
    const [valueForStyling, setValueForStyling] = useState<string>();

    const overrideClassName = getClassNameOverride(
      { dangerouslyOverrideClassName },
      'RadioGroup',
    );

    const { wrapper, input } = radioGroup({ variant });
    const isDisabled = !!(disabled || readOnly);

    useEffect(() => {
      setValueForStyling(value ? value.toString() : undefined);
    }, [value]);

    return (
      <fieldset
        className={cn('flex', 'flex-wrap', 'gap-sm', overrideClassName)}
      >
        <legend className="sr-only">{name}</legend>
        {options.map((option) => {
          const isSelected = valueForStyling === option.value;

          const inputEl = (
            <input
              ref={ref}
              type="radio"
              name={name}
              {...inputProps}
              checked={value === option.value}
              value={option.value}
              disabled={disabled}
              readOnly={readOnly}
              onChange={(e) => {
                if (disabled || readOnly) {
                  e.preventDefault();
                  e.stopPropagation();

                  return;
                }

                inputProps.onChange?.(e);
                setValueForStyling(option.value);
              }}
              className={input()}
            />
          );

          return (
            <label key={option.value}>
              {variant === 'tag' ? (
                <Tag
                  variant={isSelected ? 'active' : 'secondary'}
                  interactive={!isDisabled}
                  disabled={isDisabled}
                >
                  {inputEl}
                  {option.label}
                </Tag>
              ) : (
                <span className={wrapper()}>
                  {inputEl}
                  {option.label}
                </span>
              )}
            </label>
          );
        })}
      </fieldset>
    );
  },
);

RadioGroup.displayName = 'RadioGroup';
